import { Button, IconButton } from '@hue/design-system';
import { Minus, Plus, RotateCcw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { FIT_PHOTO, constrainPhoto, zoomPhoto, type PhotoTransform } from './photo-transform.js';

interface GestureEvent extends Event {
  scale: number;
  clientX: number;
  clientY: number;
}

/// Cada instância conserva seu próprio enquadramento. Não escala a página e não reduz a imagem
/// original a uma captura; o gesto atua sobre os bytes privados já autorizados pela API.
export function PhotoViewer({ src, alt }: { src: string; alt: string }) {
  const area = useRef<HTMLDivElement>(null);
  const image = useRef<HTMLImageElement>(null);
  const transform = useRef<PhotoTransform>({ ...FIT_PHOTO });
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef<{ points: { x: number; y: number }[]; transform: PhotoTransform } | null>(
    null,
  );
  const [shown, setShown] = useState<PhotoTransform>({ ...FIT_PHOTO });

  function frame() {
    return {
      width: area.current!.clientWidth,
      height: area.current!.clientHeight,
      imageWidth: image.current?.naturalWidth || 1,
      imageHeight: image.current?.naturalHeight || 1,
    };
  }
  function apply(value: PhotoTransform) {
    transform.current = value;
    setShown(value);
  }
  function point(x: number, y: number) {
    const rect = area.current!.getBoundingClientRect();
    return { x: x - rect.left - rect.width / 2, y: y - rect.top - rect.height / 2 };
  }
  function baseline() {
    gesture.current = { points: [...pointers.current.values()], transform: transform.current };
  }

  useEffect(() => {
    const node = area.current!;
    const frameOf = () => ({
      width: node.clientWidth,
      height: node.clientHeight,
      imageWidth: image.current?.naturalWidth || 1,
      imageHeight: image.current?.naturalHeight || 1,
    });
    const update = (value: PhotoTransform) => {
      transform.current = value;
      setShown(value);
    };
    const wheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = node.getBoundingClientRect();
      const delta =
        event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? node.clientHeight : 1);
      update(
        zoomPhoto(
          transform.current,
          transform.current.scale * Math.exp(-delta * 0.008),
          {
            x: event.clientX - rect.left - rect.width / 2,
            y: event.clientY - rect.top - rect.height / 2,
          },
          frameOf(),
        ),
      );
    };
    let initial = { ...FIT_PHOTO };
    const start = (event: Event) => {
      event.preventDefault();
      initial = transform.current;
    };
    const change = (event: Event) => {
      event.preventDefault();
      const e = event as GestureEvent,
        rect = node.getBoundingClientRect();
      update(
        zoomPhoto(
          initial,
          initial.scale * e.scale,
          { x: e.clientX - rect.left - rect.width / 2, y: e.clientY - rect.top - rect.height / 2 },
          frameOf(),
        ),
      );
    };
    node.addEventListener('wheel', wheel, { passive: false });
    node.addEventListener('gesturestart', start, { passive: false });
    node.addEventListener('gesturechange', change, { passive: false });
    const resize = new ResizeObserver(() => update(constrainPhoto(transform.current, frameOf())));
    resize.observe(node);
    return () => {
      node.removeEventListener('wheel', wheel);
      node.removeEventListener('gesturestart', start);
      node.removeEventListener('gesturechange', change);
      resize.disconnect();
    };
  }, []);

  return (
    <div className="grid min-w-0 gap-2">
      <div
        ref={area}
        role="group"
        tabIndex={0}
        aria-label={`Visualizador: ${alt}`}
        aria-keyshortcuts="ArrowLeft ArrowRight ArrowUp ArrowDown + - 0"
        className="relative h-[min(55vh,32rem)] min-h-56 touch-none overflow-hidden rounded-xl border border-surface-border bg-surface-elevated"
        style={{ cursor: shown.scale > 1 ? 'grab' : 'zoom-in' }}
        onKeyDown={(event) => {
          const moves: Record<string, [number, number]> = {
            ArrowLeft: [32, 0],
            ArrowRight: [-32, 0],
            ArrowUp: [0, 32],
            ArrowDown: [0, -32],
          };
          const move = moves[event.key];
          if (move) {
            event.preventDefault();
            apply(
              constrainPhoto(
                {
                  ...transform.current,
                  x: transform.current.x + move[0],
                  y: transform.current.y + move[1],
                },
                frame(),
              ),
            );
          } else if (event.key === '+' || event.key === '-' || event.key === '=') {
            event.preventDefault();
            apply(
              zoomPhoto(
                transform.current,
                transform.current.scale * (event.key === '-' ? 0.8 : 1.25),
                { x: 0, y: 0 },
                frame(),
              ),
            );
          } else if (event.key === '0') {
            event.preventDefault();
            apply({ ...FIT_PHOTO });
          }
        }}
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
          baseline();
        }}
        onPointerMove={(event) => {
          if (!pointers.current.has(event.pointerId) || !gesture.current) return;
          pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
          const now = [...pointers.current.values()],
            before = gesture.current.points;
          const original = gesture.current.transform;
          if (now.length >= 2 && before.length >= 2) {
            const distance = (points: { x: number; y: number }[]) =>
              Math.hypot(points[1]!.x - points[0]!.x, points[1]!.y - points[0]!.y);
            const middle = (points: { x: number; y: number }[]) => ({
              x: (points[0]!.x + points[1]!.x) / 2,
              y: (points[0]!.y + points[1]!.y) / 2,
            });
            const from = middle(before),
              to = middle(now);
            apply(
              zoomPhoto(
                original,
                (original.scale * distance(now)) / Math.max(1, distance(before)),
                point(from.x, from.y),
                frame(),
                { x: to.x - from.x, y: to.y - from.y },
              ),
            );
          } else if (now[0] && before[0]) {
            apply(
              constrainPhoto(
                {
                  ...original,
                  x: original.x + now[0].x - before[0].x,
                  y: original.y + now[0].y - before[0].y,
                },
                frame(),
              ),
            );
          }
        }}
        onPointerUp={(event) => {
          pointers.current.delete(event.pointerId);
          baseline();
        }}
        onPointerCancel={(event) => {
          pointers.current.delete(event.pointerId);
          baseline();
        }}
        onLostPointerCapture={(event) => {
          pointers.current.delete(event.pointerId);
          baseline();
        }}
      >
        <img
          ref={image}
          onLoad={() => apply(constrainPhoto(transform.current, frame()))}
          src={src}
          alt={alt}
          draggable={false}
          className="pointer-events-none size-full select-none object-contain"
          style={{ transform: `translate(${shown.x}px, ${shown.y}px) scale(${shown.scale})` }}
        />
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <IconButton
          label="Diminuir zoom"
          icon={<Minus />}
          disabled={shown.scale <= 1}
          onClick={() =>
            apply(
              zoomPhoto(transform.current, transform.current.scale / 1.25, { x: 0, y: 0 }, frame()),
            )
          }
        />
        <span className="min-w-12 text-center text-xs tabular-nums">
          {Math.round(shown.scale * 100)}%
        </span>
        <IconButton
          label="Aumentar zoom"
          icon={<Plus />}
          disabled={shown.scale >= 8}
          onClick={() =>
            apply(
              zoomPhoto(transform.current, transform.current.scale * 1.25, { x: 0, y: 0 }, frame()),
            )
          }
        />
        <Button size="sm" variant="outline" onClick={() => apply({ ...FIT_PHOTO })}>
          <RotateCcw className="size-4" /> Ajustar
        </Button>
      </div>
      <p className="text-center text-xs text-content-muted">
        Faça pinça ou use a roda para ampliar. Arraste a foto ampliada para ver detalhes.
      </p>
    </div>
  );
}
